import React from 'react';
import { useParams } from 'react-router-dom';

import { FlowComponentType } from 'routes/types';
import FinancialCheckupNavigation from 'components/FinancialCheckup/FinancialDashboardNavigation';
import ActionCard, { ActionCardSize, ActionCardVariant } from 'components/Common/Cards/ActionCard';
import {
  AvailableShift,
  useAvailableShifts,
} from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection/useAvailableShifts';
import Button, { ButtonType } from 'components/Button';
import { useNavigate } from 'hooks/useNavigate';
import useOpenShiftImpactChat from 'hooks/useOpenShiftImpactChat';
import useRequestShift from 'hooks/useRequestShift';
import { RoutePath } from 'enums/Routes';
import { ReactComponent as AIIcon } from 'images/ai-stars-icon.svg';
import { formatMonthDay, formatYearMonthDayToDate } from 'utils/dateUtils';
import { ShiftImpactSource } from 'handlers/aiChat';
import ChatWrapper from 'components/FinancialCheckup/AICoach/Wrapper';

import styles from './ShiftDetails.module.scss';

const AI_CARD_CLICK_EVENT = 'ai_impact_card_click';

type DetailRow = {
  label: string;
  value: string;
};

const getShiftDetailRows = ({
  timeRange,
  department,
  location,
  team,
  position,
  shiftType,
  staffRequired,
  staffHired,
}: AvailableShift): DetailRow[] =>
  [
    { label: 'Time', value: timeRange },
    shiftType && { label: 'Shift type', value: shiftType },
    position && { label: 'Position', value: position },
    department && { label: 'Department', value: department },
    location && { label: 'Location', value: location },
    team && { label: 'Team', value: team },
    { label: 'Staff', value: `${staffHired} / ${staffRequired}` },
  ].filter((item): item is DetailRow => Boolean(item));

const ShiftDetails = ({ navigationInfo }: FlowComponentType): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { shifts, isLoading } = useAvailableShifts();
  const openShiftImpactChat = useOpenShiftImpactChat();
  const { requestShiftSignup, isSigningUp } = useRequestShift();
  const shift = shifts?.find((s) => s.id === id) ?? null;

  if (!shift) {
    return (
      <>
        <FinancialCheckupNavigation {...navigationInfo} />
        <div className={styles.content}>
          <p className={styles.statusMessage}>{isLoading ? 'Loading...' : 'Shift not found'}</p>
        </div>
      </>
    );
  }

  const handleAICardClick = () => {
    analytics.track(AI_CARD_CLICK_EVENT, {
      shift_id: id,
    });
    openShiftImpactChat({
      shiftId: shift.shiftId,
      source: ShiftImpactSource.ShiftDetails,
      shiftRequirementId: shift.shiftRequirementId,
    });
  };

  const handleSignUp = () => {
    requestShiftSignup({
      shiftRequirementId: shift.shiftRequirementId,
      analyticsShiftId: id,
    });
  };

  const handleGoBackToDashboard = () => {
    navigate(RoutePath.FinancialDashboard, { replace: true });
  };

  const detailRows = getShiftDetailRows(shift);
  const date = formatYearMonthDayToDate(shift.date);

  return (
    <>
      <FinancialCheckupNavigation {...navigationInfo} />
      <div className={styles.content}>
        <h2 className={styles.title}>Sign up for shift</h2>

        <ActionCard
          variant={ActionCardVariant.White}
          size={ActionCardSize.Compact}
          onClick={handleAICardClick}
          showChevron
        >
          <div className={styles.aiCardRow}>
            <div className={styles.aiIcon}>
              <AIIcon />
            </div>
            <div className={styles.aiContent}>
              <p className={styles.aiTitle}>See how this shift boosts your goal.</p>
              <p className={styles.aiSubtitle}>Extra shifts help you reach your financial goals faster.</p>
            </div>
          </div>
        </ActionCard>

        <div className={styles.detailsContainer}>
          <div className={styles.dateAmountHeader}>
            <h3 className={styles.headerText}>{date ? formatMonthDay(date, { month: 'short' }) : ''}</h3>
          </div>

          <ActionCard variant={ActionCardVariant.White} className={styles.detailsCard}>
            <div className={styles.detailsContent}>
              {detailRows.map((row, index) => (
                <React.Fragment key={row.label}>
                  <div className={styles.detailRow}>
                    <p className={styles.label}>{row.label}</p>
                    <p className={styles.value}>{row.value}</p>
                  </div>
                  {index < detailRows.length - 1 && <div className={styles.divider} />}
                </React.Fragment>
              ))}
            </div>

            <div className={styles.buttonsContainer}>
              <Button onClick={handleSignUp} isLoading={isSigningUp}>
                Request this shift
              </Button>
              <Button type={ButtonType.Transparent} onClick={handleGoBackToDashboard}>
                Go back to dashboard
              </Button>
            </div>
          </ActionCard>
        </div>
      </div>
      <ChatWrapper />
    </>
  );
};

export default ShiftDetails;
